// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateRangeShortcut } from '@/constants/date-range-shortcut';
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import { getToday } from '@/utilities/date-time';
import {
  DateRange,
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';

const DEFAULT_FORMAT = 'yyyy-MM-dd';

interface Props {
  /**
   * The label attached to the input element.
   */
  label: string;

  /**
   * The date range value.
   */
  value: DateRange<DateTime>;

  /**
   * The width of the input element.
   */
  width?: string;

  /**
   * The format of the date range value.
   *
   * @default 'yyyy-MM-dd'
   */
  format?: string;

  /**
   * If provided, a collection of date range shortcuts.
   */
  shortcuts?: DateRangeShortcut[];

  /**
   * A callback fired when the date range is changed.
   */
  onChange: (dateRange: DateRange<DateTime>) => void;

  /**
   * If true, the input element can be cleared.
   */
  isClearable?: boolean;

  /**
   * If true, the input element will display in error state.
   */
  error?: boolean;

  /**
   * The helper text to display below the input when there's an error.
   */
  helperText?: string;
}

/**
 * A controlled date range input component.
 */
export function DateRangeInput(props: Props) {
  const today = getToday();
  const format = props.format ?? DEFAULT_FORMAT;
  const shortcuts = props.shortcuts?.map(({ label, getDateRange }) => ({
    label,
    getValue: () => getDateRange(today),
  }));

  return (
    <DateRangePicker
      label={props.label}
      value={props.value}
      minDate={MINIMUM_DATE}
      maxDate={MAXIMUM_DATE}
      slots={{ field: SingleInputDateRangeField }}
      format={format}
      onChange={props.onChange}
      sx={{ width: props.width }}
      slotProps={{
        field: { dateSeparator: 'to', clearable: props.isClearable },
        textField: {
          size: 'small',
          error: props.error,
          helperText: props.helperText,
          autoComplete: 'off',
        },
        shortcuts: { items: shortcuts },
      }}
    />
  );
}
